'use client';

import classNames from 'classnames';
import {
  Fragment,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import AnimateHeight from 'react-animate-height';

import { Button } from '@/components/button';
import { Link } from '@/components/link';

import { isLink } from '@/helpers/storyblok/is-link';

import './index.css';

import { CONFIG } from '@/config';

const ICON = {
  'branding-design': {
    src: '/static/icons/branding-design.svg',
    width: 32,
    height: 32,
  },
  'formation-conseil': {
    src: '/static/icons/formation-conseil.svg',
    width: 32,
    height: 32,
  },
  'renfort-equipe': {
    src: '/static/icons/renfort-equipe.svg',
    width: 32,
    height: 32,
  },
  'site-web-app': {
    src: '/static/icons/site-web-app.svg',
    width: 32,
    height: 32,
  },
};

export const MobileMenu = ({ blok, cta, storyFullSlug }) => {
  const lastScrollTop = useRef(0);
  const [open, setOpen] = useState(false);

  const [menuItemsOpen, setMenuItemsOpen] = useState(null);

  const toggle = useCallback(() => {
    lastScrollTop.current = typeof window !== 'undefined' ? window.scrollY : 0;

    setOpen((state) => !state);
  }, []);

  useLayoutEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }, [open]);

  const handleMenuItemsOpen = useCallback((menuId) => {
    setMenuItemsOpen((current) => {
      return current === menuId ? null : menuId;
    });
  }, []);

  return (
    <>
      <header
        className={classNames('mobile-menu', {
          ['mobile-menu--open']: open,
        })}
      >
        <button
          className={classNames('mobile-menu__button', {
            ['mobile-menu__button--open']: open,
          })}
          onClick={toggle}
          aria-label="Menu"
        >
          <div className="mobile-menu__button__burger">
            <span
              className={classNames(
                'mobile-menu__button__burger__line',
                'mobile-menu__button__burger__line--top',
              )}
            />
            <span
              className={classNames(
                'mobile-menu__button__burger__line',
                'mobile-menu__button__burger__line--middle',
              )}
            />
            <span
              className={classNames(
                'mobile-menu__button__burger__line',
                'mobile-menu__button__burger__line--bottom',
              )}
            />
          </div>
        </button>

        <menu className="mobile-menu__links">
          <div className="mobile-menu__links__header">
            <div className="mobile-menu__links__header__wrap">
              <Link
                href="https://quarterback.fr/"
                className="mobile-menu__logo"
              >
                <img
                  width="685"
                  height="50"
                  src="/static/quarterback-white.svg"
                  alt={CONFIG.name}
                />
              </Link>
            </div>
          </div>

          <div className="mobile-menu__top">
            <p className="mobile-menu__title">Menu</p>

            <ul className="mobile-menu__list">
              {blok.content.links.map((item) => {
                const hasSubLinks = item.subLinks?.length > 0;
                const isOpen = menuItemsOpen === item._uid;
                const El = hasSubLinks ? 'button' : Link;

                const subLinks = hasSubLinks
                  ? item.subLinks.map((subLink) => {
                      return (
                        <li
                          key={subLink._uid}
                          className="mobile-menu__sub-link"
                        >
                          {subLink.icon != '' && ICON[subLink.icon] ? (
                            <div className="mobile-menu__sub-link__icon">
                              <img
                                alt=""
                                draggable="false"
                                {...ICON[subLink.icon]}
                              />
                            </div>
                          ) : null}
                          <Link
                            link={subLink.link}
                            className="mobile-menu__sub-link__link"
                          >
                            <div className="mobile-menu__sub-link__title">
                              {subLink.title}
                            </div>
                          </Link>
                        </li>
                      );
                    })
                  : null;

                return (
                  <Fragment key={item._uid}>
                    {item.title ? (
                      <li
                        key={item._uid}
                        className={classNames('mobile-menu__item', {
                          ['mobile-menu__link--open']: isOpen,
                        })}
                      >
                        <El
                          link={isLink(item.link) ? item.link : null}
                          className={classNames('mobile-menu__link', {
                            ['mobile-menu__link--current']:
                              item.link.cached_url === storyFullSlug,

                            ['mobile-menu__link--has-sub-links']: hasSubLinks,
                          })}
                          onClick={
                            hasSubLinks
                              ? () => {
                                  handleMenuItemsOpen(item._uid);
                                }
                              : undefined
                          }
                        >
                          <div className="mobile-menu__link__title">
                            {item.title}

                            {hasSubLinks ? (
                              <span>{subLinks.length}</span>
                            ) : null}
                          </div>
                        </El>

                        {hasSubLinks ? (
                          <AnimateHeight
                            duration={300}
                            height={isOpen ? 'auto' : 0}
                            easing="ease-in-out"
                          >
                            <ul className="mobile-menu__sub-links">
                              {subLinks}
                            </ul>
                          </AnimateHeight>
                        ) : null}
                      </li>
                    ) : null}
                  </Fragment>
                );
              })}
            </ul>
          </div>

          {cta?.length > 0 ? (
            <div className="mobile-menu__ctas">
              {(cta || []).map((item) => {
                return (
                  <Button
                    key={item._uid}
                    link={item.link}
                    theme={item.theme || 'primary'}
                    height="mobile-cta"
                    fullWidth
                  >
                    {item.title}
                  </Button>
                );
              })}
            </div>
          ) : null}
        </menu>
      </header>
    </>
  );
};
